import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardBody,
  Button,
  Table,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../axiosInstance";
import { URL } from "../../api.jsx";

const AssignedCases = () => {
  const callerId = localStorage.getItem("userId");
  const navigate = useNavigate();

  /* ================= STATES ================= */
  const [cases, setCases] = useState([]);


  // FILTER
  const [filterType, setFilterType] = useState("");
  const [filterValue, setFilterValue] = useState("");

  // PAGINATION
  const [page, setPage] = useState(0);
  const [size] = useState(10);

// LOADING ✅ (THIS WAS MISSING)
const [loading, setLoading] = useState(false);

  // PAYMENT MODAL
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [copyToast, setCopyToast] = useState(false);

  // MARK SUCCESS MODAL
const [successModal, setSuccessModal] = useState(false);
const [recoveryAmount, setRecoveryAmount] = useState("");
const [selectedLoan, setSelectedLoan] = useState(null);


  /* ================= API ================= */
  const fetchAssignedCases = async (pageNo = 0) => {
  try {
    setLoading(true);

    const params = {
      callerId,
      page: pageNo,
      size,
    };

    if (filterType === "loan_id") params.loanId = filterValue;
    if (filterType === "phone") params.phoneNumber = filterValue;

    const res = await axiosInstance.get(URL.CASES_ASSIGNED, { params });

    let list = [];
    if (Array.isArray(res.data)) list = res.data;
    else if (Array.isArray(res.data?.content)) list = res.data.content;

    setCases(list);
    setPage(pageNo);
  } catch (e) {
    toast.error("Failed to load assigned cases");
  } finally {
    setLoading(false); // ✅ VERY IMPORTANT
  }
};



  /* ================= INIT ================= */
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    fetchAssignedCases(0);
  }, []);

  /* ================= FILTER HANDLERS ================= */
  const handleSearch = () => fetchAssignedCases(0);

  const handleReset = () => {
    setFilterType("");
    setFilterValue("");
    fetchAssignedCases(0);
  };

  /* ================= PAYMENT ================= */
  const openPaymentPopup = (row) => {
    setSelectedRow(row);
    setAmount(row.overdueoutstanding);
    setPaymentLink("");
    setModalOpen(true);
  };

  const handleGenerateLink = async () => {
    if (!amount) return;

    const res = await axiosInstance.get(URL.PAYMENT_LINK, {
      params: {
        customerId: selectedRow.customerid,
        loanId: selectedRow.loanid,
        amount,
        callbackUrl: "https://peday.money/",
      },
    });

    setPaymentLink(res.data?.link || "");
  };

  const openSuccessPopup = (row) => {
  setSelectedLoan(row);
  setRecoveryAmount("");
  setSuccessModal(true);
};

const handleMarkSuccess = async () => {
  if (!recoveryAmount) {
    toast.error("Please enter recovered amount");
    return;
  }

  try {
    await axiosInstance.post(
      URL.CASE_MARK_SUCCESS,
      { recoveredAmount: Number(recoveryAmount) },

      {
        params: { loanId: selectedLoan.loanid },
      }
    );

    toast.success("Case marked as success");
    setSuccessModal(false);
    fetchAssignedCases(page); // refresh list
  } catch (e) {
    toast.error("Failed to mark success");
  }
};


const handleCopy = () => {
  navigator.clipboard.writeText(paymentLink);
  toast.success("Link copied to clipboard");
};

  /* ================= UI ================= */
  return (
    <>
      {/* ================= FILTER ================= */}
      <Card className="mb-3 p-3">
        <div className="d-flex gap-2 align-items-end flex-wrap justify-content-between">
          <div className="d-flex gap-2 align-items-end flex-wrap">
            <div>
              <Label>Filter Type</Label>
              <select
                className="form-select form-select-sm"
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setFilterValue("");
                }}
              >
                <option value="">Select</option>
                <option value="loan_id">Loan ID</option>
                <option value="phone">Phone Number</option>
              </select>
            </div>

            {filterType && (
              <div>
                <Label>
                  {filterType === "loan_id" ? "Loan ID" : "Phone Number"}
                </Label>
                <Input
                  bsSize="sm"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />
              </div>
            )}

            <Button size="sm" color="info" onClick={handleSearch}>
              Search
            </Button>

            <Button size="sm" color="secondary" onClick={handleReset}>
              Reset
            </Button>
          </div>

        </div>
      </Card>

      {/* ================= TABLE ================= */}
      <Card>
        <CardBody>
          {loading ? (
            <Spinner />
          ) : (
            <Table bordered hover size="sm" responsive className="text-nowrap">
              <thead>
                <tr>
                  <th>Loan ID</th>
                  <th>Customer ID</th>
                  <th>Customer Name</th>
                  <th>Fintech Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Overdue Outstanding</th>
                  <th>Aging</th>
                  <th>Caller Name</th>
                  <th>Status</th>
                  <th>Assigned Date</th>
                  <th>Last Update Date</th>
                  <th>Pay</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {cases.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center">
                      No records found
                    </td>
                  </tr>
                ) : (
                  cases.map((c, i) => (
                    <tr key={i}>
                      <td>{c.loanid}</td>
                      <td>{c.customerid}</td>
                      <td>{c.customername}</td>
                      <td>{c.fintech_name}</td>
                      <td>{c.phonenumber}</td>
                      <td>{c.email}</td>
                      <td>{c.overdueoutstanding}</td>
                      <td>{c.aging}</td>
                      <td>{c.callername}</td>
                      <td>
                        <span className="badge bg-warning text-dark">
                          {c.status}
                        </span>
                      </td>
                      <td>{c.assigneddate}</td>
                      <td>{c.lastUpdated}</td>

                      {/* PAY COLUMN */}
                      <td>
                        <Button
                          size="sm"
                          color="success"
                          onClick={() => openPaymentPopup(c)}
                        >
                          Link
                        </Button>
                      </td>

                     <td className="d-flex gap-1">
                    <Button
                        size="sm"
                        color="info"
                        outline
                        onClick={() =>
                        navigate(`/CustomerView?customer_id=${c.customerid}`)
                        }
                    >
                        View
                    </Button>

                    {c.status === "PENDING" && (
                        <Button
                        size="sm"
                        color="success"
                        onClick={() => openSuccessPopup(c)}
                        >
                        Mark Success
                        </Button>
                    )}
                    </td>

                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
      {/* ================= PAGINATION ================= */}
        <div className="d-flex justify-content-end gap-2 mt-2">
        <Button
            size="sm"
            disabled={page === 0}
            onClick={() => fetchAssignedCases(page - 1)}
        >
            Prev
        </Button>

        <span className="align-self-center">
            Page {page + 1}
        </span>

        <Button
            size="sm"
            disabled={cases.length < size}
            onClick={() => fetchAssignedCases(page + 1)}
        >
            Next
        </Button>
        </div>

                </CardBody>
            </Card>

      {/* ================= PAYMENT MODAL ================= */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="sm">
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          Make Payment
        </ModalHeader>
        <ModalBody>
          {selectedRow && (
            <>
              <p>
                <b>Customer ID:</b> {selectedRow.customerid}
              </p>
              <p>
                <b>Loan ID:</b> {selectedRow.loanid}
              </p>

              <Label>Amount</Label>
              <Input
                type="number"
                bsSize="sm"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />

              <div className="d-flex justify-content-between mt-3">
                <Button size="sm" color="info" onClick={handleGenerateLink}>
                  Generate Link
                </Button>
              </div>
               {paymentLink && (
  <div className="mt-3 p-2 border rounded bg-light d-flex align-items-center">
    <small className="text-break">
                    {paymentLink}
                    </small>

                    <Button
                    size="sm"
                    color="secondary"
                    className="ms-3"  
                    onClick={handleCopy}
                    >
                    Copy
                    </Button>
                </div>
                )}
            </>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={successModal} toggle={() => setSuccessModal(!successModal)} size="sm">
  <ModalHeader toggle={() => setSuccessModal(!successModal)}>
    Mark Case Success
  </ModalHeader>

  <ModalBody>
    {selectedLoan && (
      <>
        <p>
          <b>Loan ID:</b> {selectedLoan.loanid}
        </p>

        <Label>Recovered Amount</Label>
       <Input
        type="number"
        step="0.01"   // 👈 decimal allow
        bsSize="sm"
        value={recoveryAmount}
        onChange={(e) => setRecoveryAmount(e.target.value)}
        />

        <div className="d-flex justify-content-end mt-3">
          <Button size="sm" color="success" onClick={handleMarkSuccess}>
            Submit
          </Button>
        </div>
      </>
    )}
  </ModalBody>
</Modal>


      

<ToastContainer
  position="top-right"
  autoClose={2000}
  hideProgressBar={false}
  closeOnClick
  pauseOnHover
/>

    </>
  );
};

export default AssignedCases;
